/**
 * Utilities for helping with normalizing evaluation scores and calculating
 * the net score.
 */

import { EvalScoresV1, EvalScoresV1File } from '../types'

// This is used for individual category scores and net score
const SCORE_MAX = 10
const SCORE_MIN = 0

export const V1_SCORES = ['overall_mos', 'naturalness', 'UTMOS', 'hubert', 'speaker_similarity']
export const V1_EVAL_TYPES = [
  'female_singing',
  'male_singing',
  'male_rap',
  'female_rap',
  'male_speaking',
  'female_speaking',
  'other',
  'Average'
]

// Values used for normalizing each score type to [0, 10].
// Contains 11 values, representing [min, 10th, 20th, ... 90th, max] percentiles for each score type.
// Calculated using the `GetEvalScoreDistributions` command.
const V1_SCORES_PERCENTILES = {
  overall_mos: [
    0.3436657190322876, 2.0112056732177734, 2.393317222595215, 2.6591885089874268,
    2.8746464252471924, 3.0620808601379395, 3.24338698387146, 3.42240571975708, 3.6191329956054688,
    3.8554937839508057, 4.735639572143555
  ],
  naturalness: [
    1.0486483921607335, 2.6188445455498166, 3.002689474158817, 3.2899845937887826,
    3.5251032842530146, 3.720662110381656, 3.88142290380266, 4.020259910159641, 4.154692259099749,
    4.287985099686517, 4.685591697692871
  ],
  UTMOS: [
    1.230155153406991, 1.452937028474278, 1.6330156355268426, 1.758109934317569, 1.8618022147372055,
    1.96618191674093, 2.068695701451765, 2.181527361713557, 2.312865930919846, 2.4847208364970155,
    3.416099993718995
  ],
  // Note: hubert scores need to be reversed, because they represent distance, not similarity.
  hubert: [
    0.018382813781499863, 0.022085824981331825, 0.02309332974255085, 0.0239393450319767,
    0.02476077526807785, 0.025673337280750275, 0.026743359863758087, 0.028141869232058525,
    0.03037007711827755, 0.0344933420419693, 0.2138197124004364
  ],
  speaker_similarity: [
    0.292244553565979, 0.752244770526886, 0.7772659659385681, 0.794808566570282, 0.8081085085868835,
    0.8187947869300842, 0.8280149102210999, 0.8361307978630066, 0.8450011610984802,
    0.8555200099945068, 0.9354954957962036
  ]
}

/** Normalizes the given score to the range [0, 10] using the percentile table above. */
export const normalizeScoreV1 = (
  scoreType: keyof typeof V1_SCORES_PERCENTILES | string,
  score: number
): number => {
  let percentiles = V1_SCORES_PERCENTILES[scoreType]

  // special handling for hubert scores, which are distances and should be reversed.
  const isReversed = scoreType === 'hubert'
  if (score <= percentiles[0]) return isReversed ? SCORE_MAX : SCORE_MIN
  if (score >= percentiles[percentiles.length - 1]) return isReversed ? SCORE_MIN : SCORE_MAX

  // Find the two closest percentiles, then use linear interpolation between them.
  let upperIdx = 1
  while (score > percentiles[upperIdx]) {
    upperIdx++
  }
  const lowerIdx = upperIdx - 1
  const lowerPercentileScore = percentiles[lowerIdx]
  const upperPercentileScore = percentiles[upperIdx]

  const amountBetweenScores =
    (score - lowerPercentileScore) / (upperPercentileScore - lowerPercentileScore)

  let normalizedScore = lowerIdx + amountBetweenScores

  if (isReversed) normalizedScore = SCORE_MAX - normalizedScore
  normalizedScore = +normalizedScore.toFixed(1)

  return normalizedScore
}

/**
 * Calculates the net V1 score from a set of eval scores.
 *
 * The formula is to take the average of all normalized scores, and then normalize.
 * [0,10] rounded to 1 decimal place.
 */
export const getNetScoreV1 = (evalScores: EvalScoresV1): number | null => {
  const averageScores = evalScores.averages
  if (!averageScores) return null

  return getNetScoreForRow(averageScores)
}

/** Gets the net score for a single row in the scores table. [0,10] rounded to 1 decimal place. */
export const getNetScoreForRow = (scores: EvalScoresV1File): number => {
  const normalizedScores = V1_SCORES.filter((key) => !!scores[key]).map((key) =>
    normalizeScoreV1(key, scores[key])
  )

  const netScore = normalizedScores.reduce((acc, score) => acc + score, 0) / normalizedScores.length
  return +netScore.toFixed(1)
}

/**
 * Gets the row from the EvalScores v1 list for the given scoreType (e.g. `female_singing`). Returns
 * undefined iit doesn't exist.
 */
export const getV1Row = (scoreType: string, scores: EvalScoresV1): EvalScoresV1File | undefined => {
  return scores.categories.find((category) => category.name === scoreType)?.averageScores
}
